/** @jsxImportSource preact */
import type { EnvelopeAction } from '../types';
import type { ActionHandler } from './types';

interface ActionButtonProps {
  action: EnvelopeAction;
  onAction: ActionHandler;
  disabled?: boolean;
}

/**
 * Renders an EnvelopeAction (tool-call shortcut) as a button.
 *
 * On click it emits a structured `envelope-action` intent up to the
 * host. The host translates that into the public `tool_intent` wire
 * body.
 *
 * Validation: an action is considered valid when it has a non-empty
 * tool name and an args_schema_ref. We DON'T attempt schema validation
 * client-side — the contract lives in args_schema_ref and is
 * authoritative server-side.
 */
function isActionValid(action: EnvelopeAction | undefined): boolean {
  if (!action) return false;
  if (typeof action.tool !== 'string' || action.tool.length === 0) return false;
  if (typeof action.label !== 'string' || action.label.length === 0) return false;
  return true;
}

export function ActionButton({ action, onAction, disabled }: ActionButtonProps) {
  const valid = isActionValid(action);
  const isDisabled = disabled === true || !valid;

  return (
    <button
      type="button"
      class="lc-envelope-action"
      disabled={isDisabled}
      data-action-id={action?.id || ''}
      data-tool={action?.tool || ''}
      onClick={() => {
        if (isDisabled) return;
        onAction({ kind: 'envelope-action', action });
      }}
    >
      {action?.label || ''}
    </button>
  );
}
